export const CONSENT_STORAGE_KEY = "vunora_consent_v1";

export type ConsentChoice = "unset" | "accepted" | "rejected";

export interface AnalyticsConfig {
  ga4MeasurementId: string;
  googleAdsId: string;
  googleAdsLeadLabel: string;
  metaPixelId: string;
}

export interface AnalyticsRuntime {
  loadScript(src: string): void;
  google(...args: unknown[]): void;
  meta(...args: unknown[]): void;
}

export interface MetaQueue {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  loaded: boolean;
  push: MetaQueue;
  queue: unknown[][];
  version: "2.0";
}

export function createMetaQueue(): MetaQueue {
  const queue = ((...args: unknown[]) => {
    if (queue.callMethod) {
      queue.callMethod(...args);
      return;
    }
    queue.queue.push(args);
  }) as MetaQueue;
  queue.loaded = true;
  queue.push = queue;
  queue.queue = [];
  queue.version = "2.0";
  return queue;
}

function configured(value: string): boolean {
  const trimmed = value.trim();
  return Boolean(trimmed) && !/^(?:GA4_|GOOGLE_ADS|META_PIXEL|YOUR_)/.test(trimmed);
}

function safely(run: () => void): void {
  try {
    run();
  } catch {
    // Ad blockers, missing globals, and rejected storage are expected conditions.
  }
}

export function createAnalytics(config: AnalyticsConfig, runtime: AnalyticsRuntime) {
  let consent: ConsentChoice = "unset";
  let initialized = false;

  safely(() => runtime.google("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  }));
  if (configured(config.metaPixelId)) {
    safely(() => runtime.meta("consent", "revoke"));
  }

  const initialize = () => {
    if (initialized || consent !== "accepted") return;
    initialized = true;
    const googleId = configured(config.ga4MeasurementId)
      ? config.ga4MeasurementId
      : configured(config.googleAdsId)
        ? config.googleAdsId
        : "";

    if (googleId) {
      safely(() => runtime.loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleId)}`));
      safely(() => runtime.google("js", new Date()));
      if (configured(config.ga4MeasurementId)) {
        safely(() => runtime.google("config", config.ga4MeasurementId));
      }
      if (configured(config.googleAdsId)) {
        safely(() => runtime.google("config", config.googleAdsId));
      }
    }

    if (configured(config.metaPixelId)) {
      safely(() => runtime.loadScript("https://connect.facebook.net/en_US/fbevents.js"));
      safely(() => runtime.meta("init", config.metaPixelId));
      safely(() => runtime.meta("track", "PageView"));
    }
  };

  return {
    getConsent: () => consent,
    setConsent(choice: Exclude<ConsentChoice, "unset">) {
      consent = choice;
      safely(() => runtime.google("consent", "update", {
        analytics_storage: choice === "accepted" ? "granted" : "denied",
        ad_storage: choice === "accepted" ? "granted" : "denied",
        ad_user_data: choice === "accepted" ? "granted" : "denied",
        ad_personalization: choice === "accepted" ? "granted" : "denied",
      }));
      if (configured(config.metaPixelId)) {
        safely(() => runtime.meta("consent", choice === "accepted" ? "grant" : "revoke"));
      }
      initialize();
    },
    track(name: string, params: Record<string, unknown> = {}) {
      if (consent !== "accepted" || !configured(config.ga4MeasurementId)) return;
      safely(() => runtime.google("event", name, params));
    },
    trackCreatedLead() {
      if (consent !== "accepted") return;
      if (configured(config.ga4MeasurementId)) {
        safely(() => runtime.google("event", "generate_lead", { event_category: "waitlist", value: 1 }));
      }
      if (configured(config.googleAdsId) && configured(config.googleAdsLeadLabel)) {
        safely(() => runtime.google("event", "conversion", {
          send_to: `${config.googleAdsId}/${config.googleAdsLeadLabel}`,
        }));
      }
      if (configured(config.metaPixelId)) {
        safely(() => runtime.meta("track", "Lead"));
      }
    },
  };
}

declare global {
  interface Window {
    dataLayer?: unknown[][];
    fbq?: MetaQueue;
    _fbq?: MetaQueue;
  }
}

export function createBrowserAnalyticsRuntime(): AnalyticsRuntime {
  window.dataLayer = window.dataLayer || [];
  if (!window.fbq) {
    window.fbq = createMetaQueue();
  }
  window._fbq = window.fbq;

  return {
    loadScript(src) {
      const script = document.createElement("script");
      script.async = true;
      script.src = src;
      document.head.appendChild(script);
    },
    google(...args) {
      window.dataLayer?.push(args);
    },
    meta(...args) {
      window.fbq?.(...args);
    },
  };
}
